// app/apps/campsite-explorer/components/SavedCampsites.tsx
'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { FaTrash, FaDirections, FaPhone, FaCalendarAlt } from 'react-icons/fa';

interface SavedCampsite {
  name: string;
  url: string;
  savedAt: string;
  latitude?: string;
  longitude?: string;
  phone?: string;
  booking_url?: string;
}

export default function SavedCampsites() {
  const [savedCampsites, setSavedCampsites] = useState<SavedCampsite[]>([]);

  useEffect(() => {
    loadSavedCampsites();
  }, []);

  const loadSavedCampsites = () => {
    const saved = JSON.parse(localStorage.getItem('savedCampsites') || '[]');
    setSavedCampsites(saved);
  };

  const removeCampsite = (url: string) => {
    const filtered = savedCampsites.filter(campsite => campsite.url !== url);
    localStorage.setItem('savedCampsites', JSON.stringify(filtered));
    setSavedCampsites(filtered);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (savedCampsites.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">No Saved Campsites</h2>
        <p className="text-gray-600 mb-6">
          You haven't saved any campsites yet. Start exploring and save your favorite spots!
        </p>
        <Link 
          href="/"
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
        >
          Explore Campsites
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">Saved Campsites ({savedCampsites.length})</h2>
      
      <div className="space-y-4">
        {savedCampsites.map((campsite) => (
          <div key={campsite.url} className="border rounded-lg p-4 hover:shadow-md transition">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <Link 
                  href={campsite.url}
                  className="text-lg font-semibold text-blue-600 hover:text-blue-800 block mb-2"
                >
                  {campsite.name}
                </Link>
                
                <p className="text-sm text-gray-500 mb-3">
                  Saved on {formatDate(campsite.savedAt)}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {campsite.booking_url && (
                    <a
                      href={campsite.booking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm bg-green-100 text-green-700 px-3 py-1 rounded-lg hover:bg-green-200 transition"
                    >
                      <FaCalendarAlt className="mr-1" />
                      Book
                    </a>
                  )}
                  
                  {campsite.phone && (
                    <a
                      href={`tel:${campsite.phone}`}
                      className="inline-flex items-center text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-lg hover:bg-blue-200 transition"
                    >
                      <FaPhone className="mr-1" />
                      Call
                    </a>
                  )}
                  
                  {campsite.latitude && campsite.longitude && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${campsite.latitude},${campsite.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-lg hover:bg-purple-200 transition"
                    >
                      <FaDirections className="mr-1" />
                      Directions
                    </a>
                  )}
                </div>
              </div>
              
              <button
                onClick={() => removeCampsite(campsite.url)}
                className="ml-4 text-red-500 hover:text-red-700 p-2"
                aria-label="Remove from saved"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}